import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { compressImage } from "@/utils/compressImage";
import { resolveActiveSpace } from "@/hooks/useActiveSpace";
import { invalidateMemories } from "@/hooks/useMemories";
import { invalidateEntitlements } from "@/hooks/useEntitlements";
import { invalidateInboxCount } from "@/hooks/queries/useInboxCount";
import { invalidateInboxItems } from "@/hooks/queries/useInboxItems";

export type SaveDraftArtifact =
  | { kind: "voice"; audioBlob: Blob; audioDurationSeconds: number }
  | { kind: "photo"; imageFile: File }
  | {
      kind: "shortie";
      videoBlob: Blob;
      thumbnailBlob?: Blob;
      videoDuration: number;
      videoMimeType: string;
    };

export interface SaveDraftInput {
  artifact: SaveDraftArtifact;
  memoryType: string | undefined;
  contextMode: "voice" | "written" | undefined;
  capsuleState: "default" | "time_capsule";
}

export interface VoiceUploadInput {
  audioBlob: Blob;
  contextBlob?: Blob;
}

export interface VoiceUploadResult {
  audioStoragePath: string;
  contextAudioPath?: string;
}

/**
 * Pipelines for the unified capture flow:
 *   • saveDraft — full "Finish Later" pipeline (auth + storage upload(s) +
 *     memories.insert + invalidations) for voice, photo, or shortie drafts.
 *   • uploadVoiceArtifacts — mid-flow voice main upload, with optional paired
 *     context audio (same timestamp, voice-context prefix). Context errors are
 *     swallowed; main audio errors throw.
 *   • uploadContextAudio — standalone context-audio upload used by photo and
 *     shortie mid-flow handlers. Throws on error; caller decides whether to
 *     surface a toast (photo toasts, shortie silently drops the path).
 */
export function useCaptureSave() {
  const saveDraftMutation = useMutation({
    mutationFn: async ({
      artifact,
      memoryType,
      contextMode,
      capsuleState,
    }: SaveDraftInput) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const space = await resolveActiveSpace(user.id);
      if (!space) throw new Error("No active space");

      const insert: Record<string, unknown> = {
        user_id: user.id,
        space_id: space.id,
        memory_type: memoryType,
        context_mode: contextMode ?? null,
        status: "pending",
        capsule_state: capsuleState,
      };

      if (artifact.kind === "voice") {
        const path = `${user.id}/${Date.now()}.webm`;
        const { error } = await supabase.storage
          .from("audio-memories")
          .upload(path, artifact.audioBlob, { contentType: "audio/webm" });
        if (error) throw new Error(error.message);
        insert.audio_url = path;
        insert.audio_duration_seconds = artifact.audioDurationSeconds;
      } else if (artifact.kind === "photo") {
        const compressed = await compressImage(artifact.imageFile);
        const path = `${user.id}/${Date.now()}.jpg`;
        const { error } = await supabase.storage
          .from("memory-images")
          .upload(path, compressed, { contentType: "image/jpeg" });
        if (error) throw new Error(error.message);
        insert.image_url = path;
      } else {
        const tempId = crypto.randomUUID();
        const ext = artifact.videoMimeType?.includes("mp4") ? "mp4" : "webm";
        const vPath = `${user.id}/${tempId}.${ext}`;
        const tPath = artifact.thumbnailBlob
          ? `${user.id}/${tempId}_thumb.jpg`
          : null;
        const [vRes] = await Promise.all([
          supabase.storage
            .from("videos")
            .upload(vPath, artifact.videoBlob, {
              contentType: artifact.videoMimeType,
            }),
          tPath && artifact.thumbnailBlob
            ? supabase.storage
                .from("memory-images")
                .upload(tPath, artifact.thumbnailBlob, {
                  contentType: "image/jpeg",
                })
            : Promise.resolve({ error: null }),
        ]);
        if (vRes.error) throw new Error(vRes.error.message);
        insert.video_url = vPath;
        if (tPath) insert.thumbnail_url = tPath;
        insert.video_duration_seconds = artifact.videoDuration;
      }

      const { error: dbErr } = await supabase.from("memories").insert(insert as never);
      if (dbErr) throw new Error(dbErr.message);
    },
    onSuccess: () => {
      void invalidateMemories();
      void invalidateEntitlements();
      void invalidateInboxCount();
      void invalidateInboxItems();
    },
  });

  const uploadVoiceArtifactsMutation = useMutation({
    mutationFn: async ({
      audioBlob,
      contextBlob,
    }: VoiceUploadInput): Promise<VoiceUploadResult> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");

      // Pair main + context audio under the same timestamp so cleanup logic
      // and any future correlation can match the two artifacts.
      const timestamp = Date.now();
      const path = `${user.id}/${timestamp}.webm`;
      const { error } = await supabase.storage
        .from("audio-memories")
        .upload(path, audioBlob, { contentType: "audio/webm" });
      if (error) throw new Error(error.message);

      let contextAudioPath: string | undefined;
      if (contextBlob) {
        // Voice-context audio lives under a dedicated prefix so
        // cleanup-expired-audio can enforce the 1-day retention without
        // touching the memory's main audio artifact. First folder stays
        // the userId to satisfy the audio-memories RLS rule.
        const ctxPath = `${user.id}/voice-context/${timestamp}.webm`;
        const { error: ctxErr } = await supabase.storage
          .from("audio-memories")
          .upload(ctxPath, contextBlob, { contentType: "audio/webm" });
        if (!ctxErr) contextAudioPath = ctxPath;
      }
      return { audioStoragePath: path, contextAudioPath };
    },
  });

  const uploadContextAudioMutation = useMutation({
    mutationFn: async (blob: Blob): Promise<string> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const path = `${user.id}/voice-context/${Date.now()}.webm`;
      const { error } = await supabase.storage
        .from("audio-memories")
        .upload(path, blob, { contentType: "audio/webm" });
      if (error) throw new Error(error.message);
      return path;
    },
  });

  return {
    saveDraft: saveDraftMutation.mutateAsync,
    uploadVoiceArtifacts: uploadVoiceArtifactsMutation.mutateAsync,
    uploadContextAudio: uploadContextAudioMutation.mutateAsync,
    isUploading:
      saveDraftMutation.isPending ||
      uploadVoiceArtifactsMutation.isPending ||
      uploadContextAudioMutation.isPending,
    error:
      saveDraftMutation.error ??
      uploadVoiceArtifactsMutation.error ??
      uploadContextAudioMutation.error ??
      null,
  };
}
